import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper to fetch and convert image to Base64 to bypass CORS in html2canvas
const getBase64Image = async (url, functionsInstance) => {
    try {
        // 1. Try Direct Fetch (Works if CORS is set up)
        const response = await fetch(url, { mode: 'cors' });
        if (response.ok) {
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        }
        throw new Error("Direct fetch failed");
    } catch (error) {
        console.warn("Direct fetch failed, trying proxy...", error);

        // 2. Try Firebase Proxy (if functionsInstance provided)
        if (functionsInstance) {
            try {
                const { httpsCallable } = await import('firebase/functions');
                const proxyImage = httpsCallable(functionsInstance, 'proxyImage');
                const result = await proxyImage({ url });
                if (result.data && result.data.base64) {
                    return result.data.base64; // Usually returns data:image/jpeg;base64,... or just base64
                }
            } catch (proxyError) {
                console.error("Proxy fetch also failed:", proxyError);
            }
        }
        return null;
    }
};

// Helper: Resize and Compress Image for optimization (if needed)
// For html2canvas approach, simple base64 is usually enough, but resizing saves memory.
const waitForImages = (element) => {
    const images = element.querySelectorAll('img');
    const promises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
        });
    });
    return Promise.all(promises);
};

export const exportProfilesToPDF = async (profiles, setExportProgress, showNotification, functionsInstance, includeAdminNotes = false) => {
    try {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = 210;
        const pageHeight = 297;

        const verifiedProfiles = profiles.filter(p => p.profileComplete);

        if (verifiedProfiles.length === 0) {
            showNotification("No verified profiles found to export.", "warning");
            return;
        }

        showNotification(`Starting export for ${verifiedProfiles.length} profiles...`, "info");

        // Create container
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.top = '-9999px';
        container.style.left = '-9999px';
        container.style.width = '210mm';
        document.body.appendChild(container);

        for (let i = 0; i < verifiedProfiles.length; i++) {
            const profile = verifiedProfiles[i];

            // PRE-FETCH IMAGE AS BASE64
            let profileImageBase64 = null;
            if (profile.images && profile.images.length > 0) {
                profileImageBase64 = await getBase64Image(profile.images[0], functionsInstance);
                // If proxy returned raw base64 without prefix, add it (assuming jpeg as most common or from previous proxy logic)
                if (profileImageBase64 && !profileImageBase64.startsWith('data:')) {
                    profileImageBase64 = `data:image/jpeg;base64,${profileImageBase64}`;
                }
            }

            // --- PAGE 1: Full Size Image ---
            container.innerHTML = `
                <div style="width: 210mm; min-height: 297mm; background: white; padding: 20mm; box-sizing: border-box; font-family: sans-serif; display: flex; flex-direction: column; align-items: center;">
                    <div style="border: 2px solid #000; width: 100%; height: 100%; flex: 1; display: flex; flex-direction: column; align-items: center; padding: 5mm;">
                        <h2 style="margin-top: 5mm; margin-bottom: 5mm; font-size: 24px; font-weight: bold; text-align: center;">Profile Picture: ${profile.name}</h2>
                        <div style="flex: 1; display: flex; align-items: center; justify-content: center; width: 100%; overflow: hidden;">
                            ${profileImageBase64
                    ? `<img src="${profileImageBase64}" style="max-width: 100%; max-height: 220mm; object-fit: contain; border: 1px solid #ccc;"/>`
                    : `<div style="font-size: 20px; color: #666;">(No Profile Picture Available)</div>`
                }
                        </div>
                    </div>
                    <div style="margin-top: auto; font-size: 10px; color: #999;">Generated by MyLuckySpouse.com</div>
                </div>
            `;

            await waitForImages(container);

            const canvas1 = await html2canvas(container.firstElementChild, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true
            });
            const imgData1 = canvas1.toDataURL('image/jpeg', 0.95);

            if (i > 0) doc.addPage();
            doc.addImage(imgData1, 'JPEG', 0, 0, pageWidth, pageHeight);


            // --- PAGE 2: Bio Data ---
            const sections = [
                {
                    title: "Personal Details",
                    fields: [
                        { label: "Full Name", value: `${profile.name} ${profile.middleName ? profile.middleName + ' ' : ''}${profile.lastName || ''}` },
                        { label: "Date of Birth", value: profile.dob ? `${new Date(profile.dob).toLocaleDateString()} (${calculateAge(profile.dob)} yrs)` : "N/A" },
                        { label: "Birth Time", value: profile.birthTime },
                        { label: "Birth Place", value: profile.birthPlace },
                        { label: "Height", value: profile.height },
                        { label: "Marital Status", value: profile.status },
                        { label: "Religion", value: profile.religion },
                        { label: "Caste", value: profile.caste },
                        { label: "Gotra", value: profile.gotra },
                        { label: "Mother Tongue", value: profile.motherTongue },
                    ]
                },
                {
                    title: "Location & Contact",
                    fields: [
                        { label: "Permanent Address", value: profile.permanentAddress ? `${profile.permanentAddress}, ${profile.permanentCity || profile.city || ''}, ${profile.permanentState || profile.state || ''} - ${profile.permanentPinCode || profile.pinCode || ''}` : '' },
                        { label: "Current Address", value: profile.sameAddress ? "Same as Permanent Address" : (profile.currentAddress ? `${profile.currentAddress}, ${profile.currentCity || profile.city || ''}, ${profile.currentState || profile.state || ''} - ${profile.currentPinCode || profile.pinCode || ''}` : '') },
                        { label: "Country", value: profile.country || "India" },
                        { label: "Phone", value: profile.phone },
                        { label: "Email", value: profile.email },
                    ]
                },
                {
                    title: "Education & Profession",
                    fields: [
                        { label: "Education", value: profile.education },
                        { label: "Degree Name", value: profile.specificDegree },
                        { label: "Employment Type", value: profile.employmentType },
                        { label: "Profession", value: profile.profession },
                        { label: "Designation", value: profile.designation },
                        { label: "Company Name", value: profile.workPlace },
                        { label: "Business Name", value: profile.businessName },
                        { label: "Job Location", value: profile.jobLocation },
                        { label: "Annual Income", value: profile.income },
                    ]
                },
                {
                    title: "Family Details",
                    fields: [
                        { label: "Father's Name", value: profile.fatherName }, // Keeping for backward compat if used
                        { label: "Mother's Name", value: profile.motherName },
                        { label: "Parent Name", value: profile.parentName },   // New field
                        { label: "Siblings", value: profile.siblings },
                        { label: "Parent Contact", value: profile.parentPhone },
                    ]
                },
                {
                    title: "About & Preferences",
                    fields: [
                        { label: "About", value: profile.about },
                        { label: "Hobbies", value: profile.hobbies },
                        { label: "Partner Preferences", value: profile.partnerPreference || profile.partnerPreferences },
                    ]
                }
            ];

            if (includeAdminNotes && profile.adminNotes && profile.adminNotes.length > 0) {
                sections.push({
                    title: "Admin Notes (Private)",
                    fields: profile.adminNotes.map(note => ({
                        label: `${note.authorName} (${new Date(note.timestamp).toLocaleDateString()})`,
                        value: note.content
                    }))
                });
            }

            const renderSection = (section) => {
                const validFields = section.fields.filter(f => f.value);
                if (validFields.length === 0) return '';

                return `
        < div style = "margin-bottom: 5mm;" >
                        <h3 style="font-size: 16px; font-weight: bold; color: #e11d48; border-bottom: 2px solid #eee; padding-bottom: 2px; margin-bottom: 3mm;">${section.title}</h3>
                        <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
                            ${validFields.map(f => `
                                <tr>
                                    <td style="width: 35%; padding: 2px 0; font-weight: bold; vertical-align: top; color: #333;">${f.label}:</td>
                                    <td style="width: 65%; padding: 2px 0; vertical-align: top; color: #000;">${f.value}</td>
                                </tr>
                            `).join('')}
                        </table>
                    </div >
    `;
            };

            container.innerHTML = `
    < div style = "width: 210mm; min-height: 297mm; background: white; padding: 20mm; box-sizing: border-box; font-family: sans-serif; display: flex; flex-direction: column;" >
                    <div style="border: 2px solid #000; width: 100%; height: 100%; flex: 1; padding: 10mm;">
                        <h1 style="text-align: center; font-size: 28px; font-weight: bold; text-transform: uppercase; margin-bottom: 2mm;">BIO-DATA</h1>
                        <div style="width: 50mm; height: 1px; background: #000; margin: 0 auto 10mm auto;"></div>

                        ${sections.map(renderSection).join('')}
                        
                    </div>
                    <div style="margin-top: auto; font-size: 10px; color: #999; text-align: right;">Generated by MyLuckySpouse.com</div>
                </div >
    `;

            const canvas2 = await html2canvas(container.firstElementChild, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true
            });
            const imgData2 = canvas2.toDataURL('image/jpeg', 0.95);

            doc.addPage();
            doc.addImage(imgData2, 'JPEG', 0, 0, pageWidth, pageHeight);

            const currentProgress = Math.round(((i + 1) / verifiedProfiles.length) * 100);
            setExportProgress(currentProgress);

            await new Promise(resolve => setTimeout(resolve, 0));
        }

        document.body.removeChild(container);

        doc.save(`Biodata_Profiles_${new Date().toISOString().slice(0, 10)}.pdf`);
        showNotification("PDF exported with Photos and Text!", "success");

    } catch (error) {
        console.error("Export Error:", error);
        showNotification("Failed to export PDF.", "error");
    } finally {
        setExportProgress(0);
    }
};
